import React from 'react'
import { cn } from '../utils'
import { AlertCircle, CheckCircle2, Info, XCircle } from 'lucide-react'

export type AlertVariant = 'default' | 'success' | 'error' | 'warning' | 'info'

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant
  title?: string
  icon?: React.ReactNode
  onClose?: () => void
}

const variantStyles = {
  default: 'bg-background text-foreground',
  success: 'bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100',
  error: 'bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100',
  warning: 'bg-yellow-50 text-yellow-900 dark:bg-yellow-950 dark:text-yellow-100',
  info: 'bg-blue-50 text-blue-900 dark:bg-blue-950 dark:text-blue-100',
}

const variantIcons = {
  default: null,
  success: <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />,
  error: <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />,
  warning: <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />,
  info: <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />,
}

export function Alert({
  className,
  variant = 'default',
  title,
  children,
  icon,
  onClose,
  ...props
}: AlertProps) {
  const IconComponent = icon ?? variantIcons[variant]

  return (
    <div
      role="alert"
      className={cn('relative rounded-lg border p-4', variantStyles[variant], className)}
      {...props}
    >
      <div className="flex items-start gap-3">
        {IconComponent && <div className="flex-shrink-0">{IconComponent}</div>}
        <div className="flex-1">
          {title && <h5 className="mb-1 font-medium leading-none tracking-tight">{title}</h5>}
          <div className="text-sm [&_p]:leading-relaxed">{children}</div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="ml-auto -mr-1.5 -mt-1.5 inline-flex h-6 w-6 items-center justify-center rounded-md hover:bg-black/5 dark:hover:bg-white/5"
          >
            <span className="sr-only">Close</span>
            <XCircle className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5
      ref={ref}
      className={cn('mb-1 font-medium leading-none tracking-tight', className)}
      {...props}
    />
  ),
)
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-sm [&_p]:leading-relaxed', className)} {...props} />
))

AlertDescription.displayName = 'AlertDescription'

export { AlertTitle, AlertDescription }
